import type { DepthOp } from '../types';

export type Smoothing = 'none' | 'bilateral' | 'guided';

export interface DepthSettings {
  normalize: boolean;
  /** Percentile clipping for normalisation (0 = min/max). */
  clip: number;
  invert: boolean;
  near: number;
  far: number;
  gamma: number;
  smoothing: Smoothing;
  smoothRadius: number;
  blur: number;
  levels: number;
  rangeMin: number;
  rangeMax: number;
}

export const DEFAULT_DEPTH: DepthSettings = {
  normalize: true,
  clip: 0,
  invert: false,
  near: 1,
  far: 0,
  gamma: 1,
  smoothing: 'none',
  smoothRadius: 4,
  blur: 0,
  levels: 0,
  rangeMin: 0,
  rangeMax: 1,
};

/** Converts UI settings into the `DepthOp` pipeline understood by stereo-core. */
export function buildDepthOps(s: DepthSettings, width?: number, height?: number): DepthOp[] {
  const ops: DepthOp[] = [];
  if (width && height) ops.push({ op: 'resize', width, height });
  if (s.normalize) ops.push(s.clip > 0 ? { op: 'normalize', low: s.clip, high: 1 - s.clip } : { op: 'normalize' });
  if (s.invert) ops.push({ op: 'invert' });
  if (s.near < 1 || s.far > 0) ops.push({ op: 'clamp', near: Math.max(s.near, s.far + 1e-3), far: s.far });
  if (Math.abs(s.gamma - 1) > 1e-3) ops.push({ op: 'gamma', gamma: s.gamma });
  if (s.smoothing === 'bilateral' && s.smoothRadius > 0) {
    ops.push({
      op: 'bilateral',
      radius: Math.round(s.smoothRadius),
      sigma_space: s.smoothRadius / 2,
      sigma_range: 0.1,
    });
  } else if (s.smoothing === 'guided' && s.smoothRadius > 0) {
    ops.push({ op: 'guided', radius: Math.round(s.smoothRadius), eps: 1e-3 });
  }
  if (s.blur > 0) ops.push({ op: 'blur', radius: s.blur });
  if (s.levels >= 2) ops.push({ op: 'quantize', levels: Math.round(s.levels) });
  if (s.rangeMin > 0 || s.rangeMax < 1) ops.push({ op: 'range', min: s.rangeMin, max: s.rangeMax });
  return ops;
}
